/**
 * DataSource: Log de Eventos (M10) — GUPM-139
 * --------------------------------------------
 * Compute-on-read: o feed é DERIVADO de dados que já existem — leituras em
 * nível não-normal (via join `instruments`) + trocas de plantão. Zero tabela
 * nova; NÃO toca no cron de ingestão (GUPM-126, LIVE em prod). A derivação
 * pura fica em `src/domain/eventos`; aqui só I/O.
 *
 * Evolução (design): se compute-on-read não escalar, trocar por uma tabela
 * `events` (outbox) populada na escrita — muda só este arquivo.
 */

import { supabase } from '@/lib/supabase';
import {
  buildHandoverEvents,
  buildReadingEvents,
  mergeEvents,
  type HandoverLike,
  type LogEvent,
  type ReadingLike,
} from '@/domain/eventos';
import { supabaseShiftHandover } from './shift-handover';
import type { DataSourceMeta } from './types';

export interface EventosDataSource extends DataSourceMeta {
  listEvents(): Promise<LogEvent[]>;
}

interface ReadingEventRow {
  id: string;
  ts: string;
  value: number;
  level: ReadingLike['level'];
  instruments:
    | { code: string; gpx_area: string | null }
    | { code: string; gpx_area: string | null }[]
    | null;
}

function unwrapInstrument(
  raw: ReadingEventRow['instruments'],
): { code: string } | null {
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw;
}

export const computeOnReadEventos: EventosDataSource = {
  mode: 'manual',
  pendingIntegration: {
    owner: 'GeoGuard (derivado)',
    name: 'Feed de eventos compute-on-read',
    status: 'ativa',
    description:
      'O Log de Eventos é montado a partir de leituras e trocas de plantão já existentes. ' +
      'Eventos de ANOMALIA (câmeras) e SIRENE entram quando M4/M5 forem integrados.',
  },

  async listEvents() {
    // Leituras em nível não-normal → eventos de nível. `!inner` + gpx_area
    // mantém só instrumentos de fonte v2 (consistente com o resto do app).
    const { data, error } = await supabase
      .from('readings')
      .select('id, ts, value, level, instruments!inner(code, gpx_area)')
      .not('instruments.gpx_area', 'is', null)
      .not('level', 'is', null)
      .neq('level', 'NORMAL')
      .order('ts', { ascending: false })
      .limit(200)
      .returns<ReadingEventRow[]>();
    if (error) throw error;

    const readings: ReadingLike[] = (data ?? []).map((r) => {
      const inst = unwrapInstrument(r.instruments);
      return {
        id: r.id,
        ts: new Date(r.ts),
        level: r.level,
        value: r.value,
        instrumentCode: inst?.code ?? '—',
      };
    });

    // Trocas de plantão → eventos de sistema (reaproveita o datasource existente,
    // que já resolve nomes/barragem via joins).
    const handoverRows = await supabaseShiftHandover.listHandovers({});
    const handovers: HandoverLike[] = handoverRows.map((h) => ({
      id: h.id,
      completedAt: new Date(h.completedAt),
      operatorOutName: h.operatorOutName,
      operatorInId: h.operatorInId,
      operatorInName: h.operatorInName,
      damCode: h.damCode,
      endKind: h.endKind,
      endedAt: h.endedAt ? new Date(h.endedAt) : null,
      endedById: h.endedById,
      endedByName: h.endedByName,
      endReason: h.endReason,
    }));

    return mergeEvents(buildReadingEvents(readings), buildHandoverEvents(handovers));
  },
};
